/**
 * Cliente HTTP tipado, gerado a partir do OpenAPI do FastAPI.
 *
 * `npm run api:generate` regenera `schema.gen.ts` — que nunca é editado à
 * mão. Drift entre o front e o back vira erro de compilação.
 */
import createClient, { type Middleware } from 'openapi-fetch';
import { getAccessToken } from '@/core/auth';
import { config } from '@/core/config';
import { ApiError } from './errors';
import type { paths } from './schema.gen';

export const api = createClient<paths>({ baseUrl: config.apiUrl });

/** O Bearer da sessão — o mesmo no cliente tipado e no `apiFetch`. */
async function authorize(headers: Headers): Promise<void> {
  const token = await getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
}

const auth: Middleware = {
  async onRequest({ request }) {
    await authorize(request.headers);
    return request;
  },
};

api.use(auth);

type Result<D> = { data?: D; error?: unknown; response: Response };

/**
 * Desembrulha a resposta do openapi-fetch: devolve o corpo ou lança
 * `ApiError`. É o que permite ao TanStack Query tratar erro como erro.
 */
export async function unwrap<D>(request: Promise<Result<D>>): Promise<D> {
  let result: Result<D>;
  try {
    result = await request;
  } catch {
    throw ApiError.from(0, null);
  }
  if (!result.response.ok) throw ApiError.from(result.response.status, result.error);
  return result.data as D;
}

/**
 * `fetch` contra a API com a mesma base e o mesmo Bearer do `api`, para o
 * que o openapi-fetch não cobre: resposta em streaming (SSE) e rotas que
 * ainda não estão no `schema.gen.ts`.
 *
 * Resposta de erro vira `ApiError` com o envelope do back; falha de rede,
 * `ApiError` com status 0. Cancelamento pelo `signal` passa adiante como
 * `AbortError` — quem chamou precisa distinguir "parou" de "falhou".
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  await authorize(headers);
  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
  } catch (e) {
    if (init.signal?.aborted) throw e;
    throw ApiError.from(0, null);
  }
  if (!response.ok) throw ApiError.from(response.status, await response.json().catch(() => null));
  return response;
}

type JsonInit = Omit<RequestInit, 'body'> & { body?: unknown };

/** `apiFetch` com corpo e resposta em JSON. `204` devolve `undefined`. */
export async function apiJson<T>(path: string, { body, ...init }: JsonInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  const response = await apiFetch(path, {
    ...init,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return (response.status === 204 ? undefined : await response.json()) as T;
}

export type { paths, components } from './schema.gen';
